import { QueryHookOptions } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { projectsApiService } from '../../../../../../../graphql/projects/projectsApiService';
import {
  ResponseGq,
  CommandGq,
  VariablesGq,
} from '../../../../../../../graphql/projects/queries/autoTagConflicts/useGetConflictsGq';
import { useDebounceValue } from '../../../../../../../logic/otherUtilities/useDebounceValue';
import { ConflictTableState } from './useConflictTableState';
import { KeywordFilteringGq } from '../../../../../../../graphql/projects/models/KeywordFilteringGq';
const deepEqual = require('deep-equal');

export function useGetDebouncedConflicts(
  tagGroupId: number,
  amountPerPage: number,
  page: number,
  conflictTableState: ConflictTableState,
  options?: QueryHookOptions<ResponseGq, VariablesGq>,
  debounceDelay: number = 300
) {
  // Debounce filter
  const debouncedFilter = useDebounceValue<KeywordFilteringGq>(
    conflictTableState.backendFilterState,
    debounceDelay
  );

  // We need to memoize options
  // Options can't be memoized with reference equality - so we need to utilize deep equality
  // Otherwise the "debounce" step will perceive the options as new in every single render, causing an infinite loop
  // Alternatively we could skip debouncing options
  // ... but then they don't vary together with the command which causes problems in case you enter something like "skip"
  const [memoizedOptions, setMemoizedOptions] = useState(options);
  useEffect(() => {
    if (deepEqual(options, memoizedOptions)) {
      return;
    }
    setMemoizedOptions(options);
  }, [options]);

  const debouncedOptions = useDebounceValue(memoizedOptions, debounceDelay);

  // Create command
  // paging and sorting does not need to be debounced
  // it "feels" much better if these are instant
  const command = useMemo(
    (): CommandGq => ({
      amountPerPage,
      page,
      tagGroupId,
      keywordQuery: {
        filtering: debouncedFilter,
      },
    }),
    [debouncedFilter, page, amountPerPage, tagGroupId]
  );
  return projectsApiService.queries.useGetConflicts(command, debouncedOptions);
}
